'use client';

import React, { useState, useCallback } from 'react';
import {
  Brain, Zap, Activity, AlertTriangle, CheckCircle2,
  RefreshCw, ChevronDown, ChevronUp, Sparkles, Clock,
  Heart, Moon, Target, Save
} from 'lucide-react';
import { useSession } from './SessionProvider';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { adaptiveProtocolService, AIPrescription, PrescriptionExercise, PrescriptionLifestyle } from '@/lib/services/adaptiveProtocolService';
import toast from 'react-hot-toast';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PrescriptionPanelProps {
  sessionId?: string;
  onPrescriptionSaved?: (protocolId: string, prescription: AIPrescription) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PRIORITY_COLORS: Record<string, string> = {
  HIGH: '#EF4444',
  MED: '#F97316',
  LOW: '#22C55E',
};

const URGENCY_CONFIG = {
  ROUTINE: { color: '#22C55E', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', label: 'Routine Follow-up' },
  ELEVATED: { color: '#F97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.3)', label: 'Elevated Priority' },
  CRITICAL: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', label: 'Clinical Alert' },
};

function buildPrescriptionPrompt(metrics: ReturnType<typeof useSession>['metrics']): string {
  const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
  const tar = metrics.thetaPower / Math.max(metrics.alphaPower, 0.1);
  const alzheimerRisk = tar > 2.0 ? 'Severe' : tar > 1.6 ? 'Moderate' : tar > 1.2 ? 'Mild' : 'Low';
  const adhdClass = tbr > 3.5 ? 'High' : tbr > 2.5 ? 'Elevated' : tbr > 1.8 ? 'Borderline' : 'Low';

  return `You are a clinical neurophysiologist generating a post-diagnostic AI prescription for a patient.

## Session Biomarker Summary
- Theta/Alpha Ratio (TAR): ${tar.toFixed(2)} → Alzheimer Risk: ${alzheimerRisk}
- Theta/Beta Ratio (TBR): ${tbr.toFixed(2)} → ADHD Classification: ${adhdClass}
- Inter-Hemispheric Coherence: ${metrics.interHemisphericCoherence.toFixed(1)}%
- Attention Score: ${metrics.attentionScore.toFixed(1)}/100
- Cognitive Load Index: ${metrics.cognitiveLoadIndex.toFixed(1)}/100
- Delta Power: ${metrics.deltaPower.toFixed(2)} µV²
- Theta Power: ${metrics.thetaPower.toFixed(2)} µV²
- Alpha Power: ${metrics.alphaPower.toFixed(2)} µV²
- Beta Power: ${metrics.betaPower.toFixed(2)} µV²
- Gamma Power: ${metrics.gammaPower.toFixed(2)} µV²

## Instructions
Generate a structured clinical action plan as valid JSON matching this exact schema:
{
  "summary": "2-3 sentence clinical summary of findings and primary intervention focus",
  "urgencyLevel": "ROUTINE" | "ELEVATED" | "CRITICAL",
  "exercises": [
    {
      "name": "Exercise name from NeuroTrainer (N-Back, Pattern Shield, Dual-Task Navigator, Memory Matrix, Attention Spotlight, Rhythm Sync)",
      "priority": "HIGH" | "MED" | "LOW",
      "difficulty": "Neuroprotect" | "Rehabilitate" | "Enhance",
      "rationale": "1 sentence clinical rationale citing specific biomarker",
      "duration": "e.g. 15 min/day",
      "targetDeficit": "e.g. Theta excess, Alpha suppression"
    }
  ],
  "lifestyle": [
    {
      "category": "Sleep" | "Nutrition" | "Exercise" | "Stress" | "Cognitive",
      "recommendation": "Specific actionable recommendation",
      "evidence": "Brief evidence basis (1 sentence)"
    }
  ],
  "followUpRecommended": true | false,
  "followUpReason": "Reason if followUpRecommended is true, else null",
  "generatedAt": "${new Date().toISOString()}"
}

Rules:
- Include 3-5 exercises ranked by priority based on the biomarker deficits
- Include 3-4 lifestyle recommendations
- Set urgencyLevel to CRITICAL if TAR > 2.0 or TBR > 3.5 or coherence < 50%
- Set urgencyLevel to ELEVATED if TAR > 1.6 or TBR > 2.5 or attention < 45
- Otherwise ROUTINE
- Return ONLY valid JSON, no markdown, no explanation`;
}

// ─── Exercise Card ─────────────────────────────────────────────────────────────
function ExerciseCard({ ex, index }: { ex: PrescriptionExercise; index: number }) {
  return (
    <div
      className="flex items-start gap-3 p-3 rounded-xl border"
      style={{
        background: 'rgba(11,22,40,0.6)',
        borderColor: `${PRIORITY_COLORS[ex.priority]}30`,
      }}
    >
      <div
        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold"
        style={{ background: `${PRIORITY_COLORS[ex.priority]}20`, color: PRIORITY_COLORS[ex.priority] }}
      >
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[13px] font-semibold text-white">{ex.name}</span>
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: `${PRIORITY_COLORS[ex.priority]}20`, color: PRIORITY_COLORS[ex.priority] }}
          >
            {ex.priority}
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#1E3A5F]/60 text-[#8BAFD4]">
            {ex.difficulty}
          </span>
        </div>
        <p className="text-[11px] text-zinc-400 mb-1.5">{ex.rationale}</p>
        <div className="flex items-center gap-3 text-[10px] text-zinc-500">
          <span className="flex items-center gap-1"><Clock size={9} />{ex.duration}</span>
          <span className="flex items-center gap-1"><Target size={9} />{ex.targetDeficit}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Lifestyle Card ────────────────────────────────────────────────────────────
const LIFESTYLE_ICONS: Record<string, React.ReactNode> = {
  Sleep: <Moon size={12} />,
  Nutrition: <Heart size={12} />,
  Exercise: <Activity size={12} />,
  Stress: <Brain size={12} />,
  Cognitive: <Zap size={12} />,
};

function LifestyleCard({ item }: { item: PrescriptionLifestyle }) {
  return (
    <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-[#0B1628]/50 border border-[#1E3A5F]/40">
      <div className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center bg-[#1E3A5F]/60 text-[#4A7AB5]">
        {LIFESTYLE_ICONS[item.category] ?? <Heart size={12} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[10px] font-semibold text-[#8BAFD4] uppercase tracking-wide">{item.category}</span>
        </div>
        <p className="text-[12px] text-white mb-1">{item.recommendation}</p>
        <p className="text-[10px] text-zinc-500 italic">{item.evidence}</p>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function PostDiagnosticPrescription({ sessionId, onPrescriptionSaved }: PrescriptionPanelProps) {
  const { metrics } = useSession();
  const [prescription, setPrescription] = useState<AIPrescription | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedProtocolId, setSavedProtocolId] = useState<string | null>(null);
  const [showLifestyle, setShowLifestyle] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePrescription = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSavedProtocolId(null);
    try {
      const prompt = buildPrescriptionPrompt(metrics);
      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [
          { role: 'system', content: 'You are a clinical neurophysiologist. Return only valid JSON.' },
          { role: 'user', content: prompt },
        ],
        { max_completion_tokens: 1500 }
      );

      const raw = response?.choices?.[0]?.message?.content ?? '';
      // Strip markdown code fences if present
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed: AIPrescription = JSON.parse(cleaned);
      setPrescription(parsed);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate prescription';
      setError(msg);
      toast.error('Prescription generation failed');
    } finally {
      setLoading(false);
    }
  }, [metrics]);

  const savePrescription = useCallback(async () => {
    if (!prescription) return;
    setSaving(true);
    try {
      const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
      const tar = metrics.thetaPower / Math.max(metrics.alphaPower, 0.1);
      const alzheimerRisk = tar > 2.0 ? 'Severe' : tar > 1.6 ? 'Moderate' : tar > 1.2 ? 'Mild' : 'Low';
      const adhdClass = tbr > 3.5 ? 'High' : tbr > 2.5 ? 'Elevated' : tbr > 1.8 ? 'Borderline' : 'Low';

      const protocolId = await adaptiveProtocolService.saveProtocol({
        sessionId,
        prescription,
        alzheimerSeverity: alzheimerRisk,
        alzheimerScore: tar,
        adhdTbr: tbr,
        adhdClassification: adhdClass,
        avgCoherence: metrics.interHemisphericCoherence,
        avgThetaAlphaRatio: metrics.thetaAlphaRatio,
        avgAttentionScore: metrics.attentionScore,
        avgCognitiveLoad: metrics.cognitiveLoadIndex,
      });

      if (protocolId) {
        setSavedProtocolId(protocolId);
        toast.success('Protocol saved to tracker');
        onPrescriptionSaved?.(protocolId, prescription);
        window.dispatchEvent(new CustomEvent('protocol:saved', { detail: { protocolId } }));
      } else {
        toast.error('Failed to save protocol');
      }
    } catch {
      toast.error('Failed to save protocol');
    } finally {
      setSaving(false);
    }
  }, [prescription, metrics, sessionId, onPrescriptionSaved]);

  const urgencyConfig = prescription ? URGENCY_CONFIG[prescription.urgencyLevel] : null;

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.95) 0%, rgba(6,13,26,0.98) 100%)',
        borderColor: 'rgba(30,58,95,0.5)',
        boxShadow: '0 0 40px rgba(249,115,22,0.04)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b"
        style={{ borderColor: 'rgba(30,58,95,0.4)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.2), rgba(30,58,95,0.3))' }}
          >
            <Sparkles size={16} className="text-[#F97316]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold text-white">Post-Diagnostic AI Prescription</h3>
            <p className="text-[11px] text-zinc-500">AI-generated clinical action plan from session biomarkers</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {prescription && !savedProtocolId && (
            <button
              onClick={savePrescription}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10 transition-all disabled:opacity-50"
            >
              {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? 'Saving…' : 'Save to Tracker'}
            </button>
          )}
          {prescription && savedProtocolId && (
            <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/10">
              <CheckCircle2 size={12} />
              Saved
            </span>
          )}
          <button
            onClick={generatePrescription}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold text-white transition-all disabled:opacity-60 touch-manipulation active:scale-95"
            style={{
              background: loading ? 'rgba(249,115,22,0.4)' : 'linear-gradient(135deg, #F97316, #EA6C0A)',
              boxShadow: loading ? 'none' : '0 4px 16px rgba(249,115,22,0.25)',
            }}
          >
            {loading ? (
              <><RefreshCw size={12} className="animate-spin" />Analysing…</>
            ) : (
              <><Brain size={12} />{prescription ? 'Regenerate' : 'Generate Prescription'}</>
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Empty state */}
        {!prescription && !loading && !error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.15)' }}
            >
              <Brain size={28} className="text-[#F97316]/60" />
            </div>
            <p className="text-[13px] text-zinc-400 mb-1">No prescription generated yet</p>
            <p className="text-[11px] text-zinc-600">Click "Generate Prescription" to analyse current session biomarkers</p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="relative w-16 h-16 mb-4">
              <div className="absolute inset-0 rounded-full border-2 border-[#F97316]/20 animate-ping" />
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}
              >
                <Brain size={28} className="text-[#F97316] animate-pulse" />
              </div>
            </div>
            <p className="text-[13px] text-white mb-1">Analysing biomarkers…</p>
            <p className="text-[11px] text-zinc-500">GPT-5 is generating your clinical action plan</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div
            className="flex items-start gap-3 p-4 rounded-xl border"
            style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.25)' }}
          >
            <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-medium text-red-400 mb-0.5">Generation failed</p>
              <p className="text-[11px] text-zinc-500">{error}</p>
            </div>
          </div>
        )}

        {/* Prescription content */}
        {prescription && !loading && (
          <div className="space-y-5">
            {/* Urgency banner */}
            {urgencyConfig && (
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl border"
                style={{ background: urgencyConfig.bg, borderColor: urgencyConfig.border }}
              >
                <AlertTriangle size={14} style={{ color: urgencyConfig.color }} />
                <div className="flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: urgencyConfig.color }}>
                    {urgencyConfig.label}
                  </span>
                  <p className="text-[12px] text-zinc-300 mt-0.5">{prescription.summary}</p>
                </div>
              </div>
            )}

            {/* Follow-up alert */}
            {prescription.followUpRecommended && (
              <div
                className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg border"
                style={{ background: 'rgba(249,115,22,0.08)', borderColor: 'rgba(249,115,22,0.25)' }}
              >
                <Activity size={13} className="text-[#F97316] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#F97316]">
                  <span className="font-semibold">Clinician follow-up recommended: </span>
                  {prescription.followUpReason}
                </p>
              </div>
            )}

            {/* Exercises */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={13} className="text-[#F97316]" />
                <h4 className="text-[12px] font-bold text-white uppercase tracking-wide">NeuroTrainer Protocol</h4>
                <span className="text-[10px] text-zinc-500 ml-auto">{prescription.exercises.length} exercises</span>
              </div>
              <div className="space-y-2">
                {prescription.exercises.map((ex, i) => (
                  <ExerciseCard key={i} ex={ex} index={i} />
                ))}
              </div>
            </div>

            {/* Lifestyle */}
            <div>
              <button
                onClick={() => setShowLifestyle(!showLifestyle)}
                className="flex items-center gap-2 w-full mb-2 group"
              >
                <Heart size={13} className="text-[#4A7AB5]" />
                <h4 className="text-[12px] font-bold text-white uppercase tracking-wide flex-1 text-left">
                  Lifestyle Interventions
                </h4>
                <span className="text-[10px] text-zinc-500">{prescription.lifestyle.length} items</span>
                {showLifestyle ? (
                  <ChevronUp size={13} className="text-zinc-500" />
                ) : (
                  <ChevronDown size={13} className="text-zinc-500" />
                )}
              </button>
              {showLifestyle && (
                <div className="space-y-2">
                  {prescription.lifestyle.map((item, i) => (
                    <LifestyleCard key={i} item={item} />
                  ))}
                </div>
              )}
            </div>

            {/* Timestamp */}
            <p className="text-[10px] text-zinc-600 text-right">
              Generated {new Date(prescription.generatedAt).toLocaleString()}
              {savedProtocolId && <span className="text-emerald-500 ml-2">· Protocol ID: {savedProtocolId.slice(0, 8)}…</span>}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
